import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import {
  ChefHat, Package, ShoppingCart, Truck, TrendingUp, History,
  AlertTriangle, CheckCircle, Calendar, Users, DollarSign
} from "lucide-react";
import PageHeader from "../../components/PageHeader";

const BOTONES_OPERACION = [
  { icon: ChefHat, label: "Producción de hoy", path: "/panel/produccion", color: "from-amber-500 to-orange-600", testid: "btn-produccion" },
  { icon: ChefHat, label: "Recetas", path: "/panel/recetas", color: "from-emerald-500 to-emerald-700", testid: "btn-recetas" },
  { icon: Package, label: "Inventario", path: "/panel/inventario", color: "from-blue-500 to-blue-700", testid: "btn-inventario" },
  { icon: ShoppingCart, label: "Compras", path: "/panel/compras", color: "from-purple-500 to-purple-700", testid: "btn-compras" },
  { icon: Truck, label: "Proveedores", path: "/panel/proveedores", color: "from-cyan-500 to-cyan-700", testid: "btn-proveedores" },
  { icon: TrendingUp, label: "Food Cost", path: "/panel/finanzas-pro", color: "from-rose-500 to-rose-700", testid: "btn-food-cost" },
  { icon: DollarSign, label: "Utilidad", path: "/panel/finanzas-pro", color: "from-emerald-600 to-teal-700", testid: "btn-utilidad" },
  { icon: DollarSign, label: "Finanzas", path: "/panel/finanzas-pro", color: "from-stone-600 to-stone-800", testid: "btn-finanzas" },
  { icon: History, label: "Auditoría", path: "/panel/auditoria", color: "from-slate-500 to-slate-700", testid: "btn-auditoria" },
];

const OperacionDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendientes, setPendientes] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/produccion?estatus=pendiente_aprobacion`),
      ]);
      setStats(s.data);
      setPendientes((p.data || []).length);
    } catch {
      // silencioso
    }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-6">
      <PageHeader icon={ChefHat} title="Operación" subtitle="Control de cocina, inventario y costos" />

      {/* Alerta de pendientes */}
      {pendientes > 0 && (
        <button onClick={() => navigate("/panel/produccion")} className="w-full bg-amber-50 border-2 border-amber-300 rounded p-4 flex items-center gap-3 hover:bg-amber-100 transition" data-testid="alert-pendientes">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
          <div className="text-left flex-1">
            <p className="font-medium text-amber-900">{pendientes} producciones esperan tu aprobación</p>
            <p className="text-xs text-amber-700">Toca para revisar y aprobar</p>
          </div>
        </button>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Eventos próximos" value={stats?.eventos?.proximos || 0} icon={Calendar} />
        <KPI label="Producciones aprobadas" value={stats?.eventos?.total || 0} icon={CheckCircle} />
        <KPI label="Ingresos del mes" value={`$${(stats?.pagos?.total_recaudado || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`} icon={DollarSign} />
        <KPI label="Pendiente cobrar" value={`$${(stats?.pagos?.total_pendiente || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`} icon={TrendingUp} />
      </div>

      {/* Botones grandes operativos (estilo Soft Restaurant) */}
      <div>
        <p className="text-xs uppercase tracking-wide text-stone-500 mb-3 px-1">Acciones rápidas</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {BOTONES_OPERACION.map((b, i) => (
            <button
              key={i}
              onClick={() => navigate(b.path)}
              className={`bg-gradient-to-br ${b.color} text-white rounded-lg p-5 text-left hover:scale-[1.02] transition shadow-sm`}
              data-testid={b.testid}
            >
              <b.icon className="w-7 h-7 mb-2 opacity-90" />
              <p className="font-medium text-sm">{b.label}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const KPI = ({ label, value, icon: Icon }) => (
  <Card className="border-stone-200">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-stone-500">{label}</p>
          <p className="font-display text-2xl text-stone-900 mt-1">{value}</p>
        </div>
        <Icon className="w-5 h-5 text-stone-400" />
      </div>
    </CardContent>
  </Card>
);

export default OperacionDashboard;
